import React, { useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../../../../hooks/storeHooks';
import { ReactComponent as ChevronDownIcon } from '../../../../images/chevron-down.svg';
import '../../Installation.scss';
import GenericFormTab from '../../../../components/common/GenericFormTab';
import { FailureFieldConfigs } from './InstallTabFieldConfig';
import { updateObservationsData } from '../../../../services/installationHistory/InstallationHistoryService';

interface InstallFailureTabProps {
  failureData: any;
  setFailureData: (data: any) => void;
  externalValidationErrors?: { [fieldName: string]: string };
  installationId?: number;
  isEditMode?: boolean;
}

const InstallFailureTab: React.FC<InstallFailureTabProps> = ({
  failureData,
  setFailureData,
  externalValidationErrors = {},
  installationId,
  isEditMode = false,
}) => {
  const dispatch = useAppDispatch();
  const pullReasons = useAppSelector((state: any) => state.createInstalls.pullReasonsData.data) || [];
  const components = useAppSelector((state: any) => state.createInstalls.componentsData.data) || [];
  const subcomponents = useAppSelector((state: any) => state.createInstalls.subcomponentsData.data) || [];
  const failureCauses = useAppSelector((state: any) => state.createInstalls.failureCausesData.data) || [];
  const failureCauseDetails = useAppSelector((state: any) => state.createInstalls.failureCauseDetailsData.data) || [];
  const rawObservationsData = useAppSelector((state: any) => state.createInstalls.observationsData.data) || {};
  const rawObservations = rawObservationsData?.Data || [];
  const observations =
    Array.isArray(rawObservations) && rawObservations.length > 0 && rawObservations[0].Items
      ? rawObservations.flatMap((group: any) => group.Items)
      : rawObservations;

  const [expandedComponents, setExpandedComponents] = React.useState<{ [component: string]: boolean }>({});

  const mapOptions = (arr: any[]) =>
    Array.isArray(arr)
      ? arr.map((item: any) => ({ key: item.Name ?? item.Value, value: String(item.Name ?? item.Value) }))
      : [];

  const failureFieldConfigs = useMemo(() => {
    return FailureFieldConfigs.failure
      .filter((field) => {
        // Hide Observations checkbox in add mode
        if (field.name === 'Observations' && !isEditMode) {
          return false;
        }
        return true;
      })
      .map((field) => {
        if (field.name === 'ReasonForPull') {
          return { ...field, options: mapOptions(pullReasons) };
        }
        if (field.name === 'Component') {
          return { ...field, options: mapOptions(components) };
        }
        if (field.name === 'Subcomponent') {
          return { ...field, options: mapOptions(subcomponents) };
        }
        if (field.name === 'FailureCause') {
          return { ...field, options: mapOptions(failureCauses) };
        }
        if (field.name === 'FailureCauseDetail') {
          return { ...field, options: mapOptions(failureCauseDetails) };
        }
        return field;
      });
  }, [pullReasons, components, subcomponents, failureCauses, failureCauseDetails, isEditMode]);

  // Group observations by Component
  const groupedObservations = useMemo(() => {
    const groups: { [component: string]: any[] } = {};
    const obsArray = Array.isArray(observations) ? observations : [];
    obsArray.forEach((obs: any) => {
      if (!groups[obs.Component]) groups[obs.Component] = [];
      groups[obs.Component].push(obs);
    });
    return groups;
  }, [observations]);

  const toggleComponent = (component: string) => {
    setExpandedComponents((prev) => ({ ...prev, [component]: !prev[component] }));
  };

  const getAllObservationIds = () => {
    const obsArray = Array.isArray(observations) ? observations : [];
    return obsArray.map((obs: any) => obs.Id).filter((id) => id != null);
  };

  React.useEffect(() => {
    if (!isEditMode) return;

    const allIds = getAllObservationIds();
    const currentObservationIds = failureData.ObservationIds || [];

    if (failureData.Observations) {
      const missingIds = allIds.filter((id) => !currentObservationIds.includes(id));

      if (missingIds.length > 0) {
        setFailureData((prevData: any) => ({
          ...prevData,
          ObservationIds: allIds,
        }));

        dispatch(
          updateObservationsData({
            id: installationId,
            observationIds: allIds,
          }),
        );
      }
    } else {
      if (currentObservationIds.length > 0) {
        setFailureData((prevData: any) => ({
          ...prevData,
          ObservationIds: [],
        }));

        dispatch(
          updateObservationsData({
            id: installationId,
            observationIds: [],
          }),
        );
      }
    }
  }, [failureData.Observations, observations.length, isEditMode]);
  const customFailureFieldConfigs = useMemo(() => {
    return failureFieldConfigs;
  }, [failureFieldConfigs]);

  return (
    <div className='create-install-fields-container'>
      <GenericFormTab
        fieldConfigs={customFailureFieldConfigs}
        formData={failureData}
        setFormData={setFailureData}
        externalValidationErrors={externalValidationErrors}
        isCheckboxRight
      />

      {/* Custom Observations Section */}
      {isEditMode &&
        Object.entries(groupedObservations).map(([component, items]) => {
          const isExpanded = expandedComponents[component];
          return (
            <div key={component} className='install-failure__menu'>
              <div
                className={`install-failure__menu-header${isExpanded ? ' expanded' : ''}`}
                onClick={() => toggleComponent(component)}
              >
                <ChevronDownIcon
                  className='install-failure__chevron'
                  style={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
                <span>{component}</span>
              </div>
              {isExpanded && (
                <div className='install-failure__menu-list'>
                  {items.map((obs: any, idx: number) => {
                    const checked =
                      Array.isArray(failureData.ObservationIds) && failureData.ObservationIds.includes(obs.Id);
                    const handleObsCheck = () => {
                      let newIds: number[];
                      if (checked) {
                        newIds = failureData.ObservationIds.filter((id: number) => id !== obs.Id);
                      } else {
                        newIds = [...(failureData.ObservationIds || []), obs.Id];
                      }

                      const allIds = getAllObservationIds();
                      const allSelected = allIds.length > 0 && newIds.length === allIds.length;

                      setFailureData({
                        ...failureData,
                        Observations: allSelected,
                        ObservationIds: newIds,
                      });

                      dispatch(
                        updateObservationsData({
                          id: installationId,
                          observationIds: newIds,
                        }),
                      );
                    };
                    const isLast = idx === items.length - 1;
                    return (
                      <div
                        key={obs.Id || obs.Name}
                        className={`install-failure__menu-item${isLast ? ' last' : ''}${idx === 0 ? ' first' : ''}`}
                      >
                        <div
                          className={`checkbox${checked ? ' enabled' : ''} install-failure__checkbox`}
                          onClick={handleObsCheck}
                        >
                          {checked && <div className='checkbox-icon' />}
                        </div>
                        <span className='install-failure__menu-label' onClick={handleObsCheck}>
                          {obs.Name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default InstallFailureTab;
